import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import usePageTracking from "@/lib/usePageTracking";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";

import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import HowItWorks from "@/components/landing/HowItWorks";
import Services from "@/components/landing/Services";
import Features from "@/components/landing/Features";
import Audience from "@/components/landing/Audience";
import Testimonials from "@/components/landing/Testimonials";
import Pricing from "@/components/landing/Pricing";
import FAQ from "@/components/landing/FAQ";
import Waitlist from "@/components/landing/Waitlist";
import Footer from "@/components/landing/Footer";

import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ManagedInviteAcceptPage from "@/pages/ManagedInviteAcceptPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import VerifyStaffPage from "@/pages/VerifyStaffPage";
import VerifyDriverPage from "@/pages/VerifyDriverPage";
import BlogIndexPage from "@/pages/BlogIndexPage";
import BlogPostPage from "@/pages/BlogPostPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import DriverDashboard from "@/pages/DriverDashboard";
import AdminDashboard from "@/pages/AdminDashboard";

const Landing = () => (
  <div className="App font-body bg-white text-[#0A0A0A]" data-testid="landing-root">
    <Header />
    <main>
      <Hero />
      <HowItWorks />
      <Services />
      <Features />
      <Audience />
      <Testimonials />
      <Pricing />
      <FAQ />
      <Waitlist />
    </main>
    <Footer />
  </div>
);

function PageTracker() {
  usePageTracking();
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageTracker />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/managed-invite/accept" element={<ManagedInviteAcceptPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/verify-staff/:token" element={<VerifyStaffPage />} />
          <Route path="/verify-driver/:token" element={<VerifyDriverPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          <Route
            path="/dashboard/customer"
            element={
              <ProtectedRoute roles={["customer"]}>
                <CustomerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/driver"
            element={
              <ProtectedRoute roles={["driver"]}>
                <DriverDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/drivers/:driverId"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Convenience: /dashboard auto-redirects based on role */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <RoleRedirect />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

function RoleRedirect() {
  const { user } = useAuth();
  if (user && typeof user === "object") {
    return <Navigate to={`/dashboard/${user.role}`} replace />;
  }
  return null;
}

export default App;
